import { useMemo } from "react";

// THM grade heatmap ramp - must mirror processing.THM_RAMP / thm_color on the backend.
export const THM_RAMP = [
  { max: 1.5, color: "#FDECEC", label: "< 1.5%" },
  { max: 3.0, color: "#FCD34D", label: "1.5–3%" },
  { max: 4.5, color: "#F59E0B", label: "3–4.5%" },
  { max: 6.5, color: "#EA580C", label: "4.5–6.5%" },
  { max: Infinity, color: "#DC2626", label: "> 6.5%" },
];
export const THM_NODATA = "#CBD5E1";
export function thmColor(v) {
  if (v == null || isNaN(v)) return THM_NODATA;
  for (const s of THM_RAMP) if (v < s.max) return s.color;
  return "#DC2626";
}

// Model vs Ground-Truth verdict colours (navy-safe).
export const VALIDATION_C = { confirm: "#15803D", upside: "#D97706", downside: "#DC2626", neutral: "#94A3B8" };
export const VALIDATION_LABEL = { confirm: "Confirms", upside: "Upside", downside: "Downside", neutral: "No grade" };

const STRAND_C = { 1: "#67E8F9", 2: "#38BDF8", 3: "#7CA9E6", 4: "#B8C4E8" };
const RIDGE_C = "#D8A574";
const COR_C = "#FFFFFF";
const NAVY = "#0B1420";

function extractRings(boundary) {
  const geom = boundary?.geometry || boundary;
  const c = geom?.coordinates;
  if (!c) return [];
  if (geom.type === "Polygon") return c;
  if (geom.type === "MultiPolygon") return c.flat();
  return [];
}

const clampPct = (v) => Math.min(98.5, Math.max(1.5, v));

// Interactive HMS Borehole Log Overlay. Markers are coloured by a THM grade heatmap (or by their
// Model-vs-Ground-Truth verdict in validation mode). An optional interpolated THM grade surface
// (IDW, clipped to the AOI) fills continuous hot zones between holes. The selected hole shows a
// snap connector + grade popup; in section mode, clicking holes builds an ordered A–A′ selection.
export default function BoreholeOverlay({ bbox, boundary, geo, boreholes = [], gradeFields = [],
                                          thmField, selectedId, onSelect,
                                          sectionMode = false, sectionIds = [], onToggleHole,
                                          showSurface = false, validationMode = false, contours = [] }) {
  const [minx, miny, maxx, maxy] = bbox || [0, 0, 1, 1];
  const dims = useMemo(() => {
    const W = 1000;
    const midlat = (((miny + maxy) / 2) * Math.PI) / 180;
    const geoW = Math.max(1e-9, (maxx - minx) * Math.cos(midlat));
    const geoH = Math.max(1e-9, maxy - miny);
    const H = Math.max(240, Math.min(1600, Math.round((W * geoH) / geoW)));
    return { W, H };
  }, [minx, miny, maxx, maxy]);

  const sx = (lon) => ((lon - minx) / (maxx - minx || 1e-9)) * dims.W;
  const sy = (lat) => ((maxy - lat) / (maxy - miny || 1e-9)) * dims.H;
  const fx = (lon) => ((lon - minx) / (maxx - minx || 1e-9)) * 100;
  const fy = (lat) => ((maxy - lat) / (maxy - miny || 1e-9)) * 100;
  const pts = (arr) => (arr || []).map(([lo, la]) => `${sx(lo).toFixed(1)},${sy(la).toFixed(1)}`).join(" ");

  const rings = useMemo(() => extractRings(boundary), [boundary]);
  const corridors = geo?.corridors || [];
  const strandlines = geo?.strandlines || [];
  const ridges = geo?.ridges || [];
  const selected = boreholes.find((h) => h.hole_id === selectedId) || null;
  const sectionPts = sectionIds.map((id) => boreholes.find((h) => h.hole_id === id)).filter(Boolean);

  // ---- interpolated THM grade surface (client-side IDW; clipped to the licence) ----
  const surface = useMemo(() => {
    if (!showSurface || !thmField) return null;
    const graded = boreholes.filter(
      (h) => typeof h.grades?.[thmField] === "number" && h.lon != null && h.lat != null);
    if (graded.length < 3) return null;
    const cols = 48;
    const midlat = (((miny + maxy) / 2) * Math.PI) / 180;
    const cosf = Math.max(0.1, Math.cos(midlat));
    const geoW = (maxx - minx) * cosf, geoH = maxy - miny;
    const rows = Math.max(10, Math.min(90, Math.round((cols * geoH) / (geoW || 1e-9))));
    const dx = (maxx - minx) / cols, dy = (maxy - miny) / rows;
    const cellW = dims.W / cols, cellH = dims.H / rows;
    const cells = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const lon = minx + (c + 0.5) * dx, lat = maxy - (r + 0.5) * dy;
        let ws = 0, vs = 0;
        for (const h of graded) {
          const ex = (h.lon - lon) * cosf, ey = h.lat - lat;
          const d2 = ex * ex + ey * ey + 1e-12;
          const w = 1 / (d2 * d2);
          ws += w; vs += w * h.grades[thmField];
        }
        cells.push({ x: c * cellW, y: r * cellH, w: cellW + 0.6, h: cellH + 0.6, color: thmColor(vs / ws) });
      }
    }
    return cells;
  }, [showSurface, thmField, boreholes, minx, miny, maxx, maxy, dims]);

  return (
    <div className="relative w-full max-h-full overflow-hidden rounded-lg" data-testid="borehole-overlay"
      style={{ aspectRatio: `${dims.W} / ${dims.H}`, background: NAVY }}>
      <svg viewBox={`0 0 ${dims.W} ${dims.H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        <defs>
          <pattern id="bh-grid" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M60 0H0V60" fill="none" stroke="#1E293B" strokeWidth="0.6" />
          </pattern>
          <clipPath id="bh-aoi-clip">
            {rings.map((ring, i) => (<polygon key={`clip-${i}`} points={pts(ring)} />))}
          </clipPath>
        </defs>
        <rect x="0" y="0" width={dims.W} height={dims.H} fill="url(#bh-grid)" />
        {/* interpolated THM grade surface, clipped to the AOI */}
        {surface && rings.length > 0 && (
          <g clipPath="url(#bh-aoi-clip)" opacity="0.5" data-testid="borehole-surface">
            {surface.map((cell, i) => (
              <rect key={`gs-${i}`} x={cell.x} y={cell.y} width={cell.w} height={cell.h} fill={cell.color} />
            ))}
          </g>
        )}
        {/* labelled iso-grade THM contour lines over the surface */}
        {showSurface && contours?.length > 0 && rings.length > 0 && (
          <g clipPath="url(#bh-aoi-clip)" data-testid="borehole-contours">
            {contours.map((ct, ci) => ct.paths.map((path, pi) => (
              <polyline key={`ct-${ci}-${pi}`} points={pts(path)} fill="none"
                stroke="#0B1420" strokeWidth="0.8" strokeOpacity="0.55" />
            )))}
          </g>
        )}
        {corridors.map((c, i) => (c.extent?.length >= 3 ? (
          <polygon key={`ce-${i}`} points={pts(c.extent)} fill={COR_C} fillOpacity="0.14" stroke="none" />
        ) : null))}
        {strandlines.map((s, i) => (s.path?.length >= 2 ? (
          <polyline key={`s-${i}`} points={pts(s.path)} fill="none"
            stroke={STRAND_C[s.age_tier || 2] || STRAND_C[2]} strokeWidth="1.4"
            strokeDasharray="6 3" strokeOpacity="0.7" />
        ) : null))}
        {ridges.map((r, i) => (r.path?.length >= 2 ? (
          <polyline key={`r-${i}`} points={pts(r.path)} fill="none" stroke={RIDGE_C}
            strokeWidth="2" strokeOpacity="0.65" strokeLinecap="round" />
        ) : null))}
        {corridors.map((c, i) => (c.centerline?.length >= 2 ? (
          <g key={`cc-${i}`}>
            <polyline points={pts(c.centerline)} fill="none" stroke="#0B1F3A" strokeWidth="5" strokeLinecap="round" />
            <polyline points={pts(c.centerline)} fill="none" stroke={COR_C} strokeWidth="2.6"
              strokeDasharray="10 5" strokeLinecap="round" />
          </g>
        ) : null))}
        {rings.map((ring, i) => (
          <polygon key={`b-${i}`} points={pts(ring)} fill="none" stroke={COR_C} strokeWidth="2.2" strokeOpacity="0.95" />
        ))}
        {/* snap connector from the selected hole to its nearest corridor centreline */}
        {!sectionMode && selected?.snap?.point && (
          <line x1={sx(selected.lon)} y1={sy(selected.lat)} x2={sx(selected.snap.point[0])} y2={sy(selected.snap.point[1])}
            stroke="#ffffff" strokeWidth="1.6" strokeDasharray="4 3" strokeOpacity="0.9" data-testid="snap-connector" />
        )}
        {/* section poly-line A–A′ */}
        {sectionPts.length >= 2 && (
          <polyline points={pts(sectionPts.map((h) => [h.lon, h.lat]))} fill="none"
            stroke={COR_C} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"
            data-testid="section-line" />
        )}
      </svg>

      {/* iso-grade contour value labels */}
      {showSurface && (contours || []).map((ct, ci) => {
        const lp = ct.paths.reduce((a, b) => (b.length > a.length ? b : a), []);
        if (lp.length < 2) return null;
        const mid = lp[Math.floor(lp.length / 2)];
        return (
          <div key={`ctl-${ci}`} data-testid={`contour-label-${ct.level}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none text-[9px] font-bold px-1 rounded"
            style={{ left: `${clampPct(fx(mid[0]))}%`, top: `${clampPct(fy(mid[1]))}%`, zIndex: 14, background: "rgba(255,255,255,0.85)", color: "#0B1420" }}>
            {ct.level}%
          </div>
        );
      })}

      {/* corridor labels - above markers, non-interactive */}
      {corridors.map((c, i) => {
        if (c.centerline?.length < 2) return null;
        const mid = [(c.centerline[0][0] + c.centerline[1][0]) / 2, (c.centerline[0][1] + c.centerline[1][1]) / 2];
        return (
          <div key={`cl-${i}`} className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none px-1.5 py-0.5 rounded text-[10px] font-bold"
            style={{ left: `${clampPct(fx(mid[0]))}%`, top: `${clampPct(fy(mid[1]))}%`, zIndex: 28,
              background: "#0B1420", color: COR_C, border: `1px solid ${COR_C}` }}>
            {c.id}
          </div>
        );
      })}

      {/* hint when the grade surface can't be interpolated (need >= 3 graded holes) */}
      {showSurface && !surface && thmField && boreholes.length > 0 && (
        <div className="absolute top-2 left-2 z-30 pointer-events-none text-[10px] font-semibold px-2 py-1 rounded"
          style={{ background: "rgba(11,20,32,0.82)", color: "#FFFFFF", border: "1px solid #C20511" }}
          data-testid="surface-hint">
          Need ≥3 graded holes to interpolate a grade surface
        </div>
      )}

      {/* drillhole markers (THM heatmap, or validation verdict in validation mode) */}
      {boreholes.map((h, hidx) => {
        const active = h.hole_id === selectedId;
        const order = sectionIds.indexOf(h.hole_id);
        const inSec = order >= 0;
        const rx = fx(h.lon), ry = fy(h.lat);
        const inb = rx >= 0 && rx <= 100 && ry >= 0 && ry <= 100;
        const thm = thmField ? h.grades?.[thmField] : null;
        const col = validationMode ? (VALIDATION_C[h.validation] || VALIDATION_C.neutral) : thmColor(thm);
        // spread clamped out-of-AOI holes along the edge so overlapping markers stay clickable
        const jx = inb ? 0 : ((hidx % 8) - 3.5) * 2.2;
        const jy = inb ? 0 : ((Math.floor(hidx / 8) % 5) - 2) * 2.2;
        return (
          <button key={h.hole_id} data-testid={`borehole-marker-${h.hole_id}`}
            onClick={(e) => { e.stopPropagation(); sectionMode ? onToggleHole?.(h.hole_id) : onSelect?.(active ? null : h.hole_id); }}
            title={inb ? h.hole_id : `${h.hole_id} (outside AOI)`}
            className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full transition-transform hover:scale-125 focus:outline-none"
            style={{
              left: `${clampPct(rx + jx)}%`, top: `${clampPct(ry + jy)}%`,
              width: active || inSec ? 18 : 13, height: active || inSec ? 18 : 13,
              background: col,
              border: inb ? `2px solid ${active || inSec ? "#0B1420" : NAVY}` : "2px dashed #ffffff",
              opacity: inb ? 1 : 0.85,
              boxShadow: active ? "0 0 0 3px rgba(250,204,21,0.6)"
                : inSec ? "0 0 0 3px rgba(11,20,32,0.55)" : "0 1px 3px rgba(0,0,0,0.6)",
              zIndex: active ? 30 : inSec ? 26 : 15,
            }}>
            {inSec && (
              <span className="absolute -top-2 -right-2 grid place-items-center rounded-full text-[9px] font-bold"
                style={{ width: 14, height: 14, background: NAVY, color: COR_C, border: `1px solid ${COR_C}` }}>
                {order + 1}
              </span>
            )}
            {!inSec && h.has_intervals && !validationMode && (
              <span className="absolute -bottom-1 -right-1 rounded-full" title="Down-hole interval assays"
                style={{ width: 6, height: 6, background: "#0B1420", border: `1px solid ${COR_C}` }} />
            )}
          </button>
        );
      })}

      {/* compact on-map grade scale bar (heatmap) / verdict key (validation) - mirrors the PDF plate legend */}
      {thmField && !validationMode && (
        <div className="absolute bottom-2 left-2 z-30 pointer-events-none rounded-md px-2 py-1.5" data-testid="map-grade-legend"
          style={{ background: "rgba(11,20,32,0.82)", border: "1px solid #C20511" }}>
          <div className="text-[8px] font-bold uppercase tracking-wide mb-1" style={{ color: "#FFFFFF" }}>{thmField} (%)</div>
          <div className="w-[124px]">
            <div className="flex">
              {THM_RAMP.map((s, i) => (<div key={i} className="h-2 flex-1" style={{ background: s.color }} />))}
            </div>
            <div className="flex justify-between text-[7px] mt-0.5" style={{ color: "#E2E8F0" }}>
              <span>0</span><span>1.5</span><span>3</span><span>4.5</span><span>6.5+</span>
            </div>
          </div>
        </div>
      )}
      {validationMode && (
        <div className="absolute bottom-2 left-2 z-30 pointer-events-none rounded-md px-2 py-1.5 space-y-0.5" data-testid="map-validation-legend"
          style={{ background: "rgba(11,20,32,0.82)", border: "1px solid #C20511" }}>
          <div className="text-[8px] font-bold uppercase tracking-wide mb-0.5" style={{ color: "#FFFFFF" }}>Verdict</div>
          {Object.entries(VALIDATION_LABEL).map(([k, label]) => (
            <div key={k} className="flex items-center gap-1 text-[8px]" style={{ color: "#E2E8F0" }}>
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: VALIDATION_C[k] }} /> {label}
            </div>
          ))}
        </div>
      )}

      {/* grade pop-up for the selected hole */}
      {!sectionMode && selected && (() => {
        const spx = clampPct(fx(selected.lon));
        const spy = clampPct(fy(selected.lat));
        return (
          <div className="absolute z-40 pointer-events-none" style={{ left: `${spx}%`, top: `${spy}%` }} data-testid="borehole-popup">
            <div className="w-[236px] rounded-lg overflow-hidden shadow-2xl border border-[#C20511]"
              style={{ transform: `translate(${spx > 55 ? "calc(-100% - 14px)" : "14px"}, ${spy > 55 ? "calc(-100% - 14px)" : "14px"})` }}>
              <div className="flex items-center justify-between px-3 h-9" style={{ background: NAVY }}>
                <span className="text-xs font-bold" style={{ color: COR_C }}>{selected.hole_id}</span>
                <button data-testid="borehole-popup-close" onClick={(e) => { e.stopPropagation(); onSelect?.(null); }}
                  className="text-slate-300 hover:text-white text-sm leading-none pointer-events-auto">&times;</button>
              </div>
              <div className="bg-white p-3">
                <div className="text-[11px] text-slate-500 mb-1.5">
                  {selected.lat.toFixed(5)}, {selected.lon.toFixed(5)}
                  {selected.depth_m != null ? ` · ${selected.depth_m} m` : ""}
                  {selected.has_intervals ? ` · ${(selected.intervals || []).length} intervals` : ""}
                </div>
                {selected.snap && (
                  <div className="text-[11px] font-semibold mb-2" style={{ color: "#92400E" }} data-testid="popup-snap">
                    ↳ Nearest corridor {selected.snap.corridor_id} · {selected.snap.distance_m} m
                  </div>
                )}
                {selected.validation && (
                  <div className="text-[11px] font-semibold mb-2 flex items-center gap-1.5" data-testid="popup-validation">
                    <span className="w-2 h-2 rounded-full" style={{ background: VALIDATION_C[selected.validation] }} />
                    <span style={{ color: VALIDATION_C[selected.validation] }}>{VALIDATION_LABEL[selected.validation]}</span>
                  </div>
                )}
                {Object.keys(selected.grades || {}).length ? (
                  <table className="w-full text-[11px]">
                    <tbody>
                      {(gradeFields.length ? gradeFields : Object.keys(selected.grades)).map((g) => (
                        selected.grades[g] != null ? (
                          <tr key={g} className="border-b border-slate-100 last:border-0">
                            <td className="py-1 text-slate-600">
                              {g === thmField && (
                                <span className="inline-block w-2 h-2 rounded-sm mr-1 align-middle" style={{ background: thmColor(selected.grades[g]) }} />
                              )}
                              {g}
                            </td>
                            <td className="py-1 text-right font-bold text-slate-900 tabular-nums">{selected.grades[g]}</td>
                          </tr>
                        ) : null
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-[11px] text-slate-400">No grades logged for this hole.</p>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
